/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useRef, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
// eslint-disable-next-line lodash/import-scope
import type { DebouncedFunc } from 'lodash';
import type { Options } from './types.tsx';

export default function useDebouncedCallback<F extends (...args: Parameters<F>) => ReturnType<F>>(
	callback: F,
	delay: number,
	options?: Options,
): readonly [DebouncedFunc<F>, () => void] {
	const optionsRef = useRef(options);
	const callbackRef = useRef<F>(callback);
	callbackRef.current = callback;

	const debouncedCallBack = useMemo<DebouncedFunc<F>>(
		() =>
			debounce((...args: Parameters<F>) => callbackRef.current(...args), delay, optionsRef.current),
		[delay],
	);

	useEffect(() => debouncedCallBack.cancel, [debouncedCallBack]);

	return [debouncedCallBack, debouncedCallBack.cancel] as const;
}
